/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Skeleton, Tab, Tabs } from '@mui/material';
import { LuiCard, LuiPageHeader } from '@jda/lui-common-component-library-mui5';

import { useStyles } from './styles';
import { LABELS } from './constants';
import Loader from '../../shared/Loader';
import UsersGroups from "../UserGroup/Users";
import SnackbarAlert from '../../shared/SnackbarAlert';
import { getGroupIdentifier } from '../../utility/commonUtility';
import { TabPanelProps } from '../../models/Roles/RolesContracts';
import useWindowDimensions from '../../utility/useWindowDimensions';
import { AddUserGroupGeneralTab } from '../../components/UserGroups';
import { getUserGroupByIdentifierRequest } from '../../services/userGroups';
import { setSelectedTab } from '../../store/appLoader/appLoaderActionCreator';
import UserGroupRolesTab from '../../components/UserGroups/UserGroupRolesTab';
import { selectSnackBarSuccess } from '../../store/snackBar/snackBarSelector';
import { checkResourcePermission } from '../../shared/CheckPermissions/CheckPermission';
import { actions, resources } from '../../shared/CheckPermissions/constants';
import { selectResourcePermissions } from '../../store/auth/authSelector';

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      style={{ width: '100%' }}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ height: '100%' }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const AddUserGroup = () => {
  const { classes } = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { height } = useWindowDimensions();

  const selectSucccessMessage = useSelector(selectSnackBarSuccess);
  const resourcePermissions = useSelector(selectResourcePermissions);

  const [value, setValue] = useState(0);

  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [groupData, setGroupData] = useState<any>({ identifier: '', displayName: '', description: '' });

  const viewPermissionRoles =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.VIEW, resourcePermissions.resources[resources.GROUPS_ROLES]);
  const viewPermissionUsers =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.VIEW, resourcePermissions.resources[resources.GROUPS_SUBJECTS]);

  const handleChange = (event: any, newValue: number) => {
    console.log(event);
    setValue(newValue);
  };

  const getUserGroupData = async () => {
    const { userGroupIdentifier } = await getGroupIdentifier(history.location.pathname);
    if (history.location && history.location.pathname) {
      if (userGroupIdentifier) {
        setLoading(true);
        setIsEdit(true);
        getUserGroupByIdentifierRequest(userGroupIdentifier).then((res: any) => {
          if (res) setGroupData(res);
          setLoading(false);
        });
      } else {
        setLoading(false);
        setIsEdit(false);
      }
    } else {
      setLoading(false);
      setIsEdit(false);
    }
  };

  useEffect(() => {
    getUserGroupData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  };

  const handleBackButtonClick = () => {
    history.goBack();
    dispatch(setSelectedTab('User Groups'));
  };

  const isDisabledTab = groupData.identifier === '';

  return (
    <>
      <Box className={classes.root}>
        <LuiPageHeader
          title={ !isEdit ? LABELS.CREATE_USER_GROUP :
             (groupData?.identifier || 
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Skeleton variant="text" animation={'pulse'} width={150} />
                <Skeleton variant="text" animation={'pulse'} width={300} />
              </Box>)
          }
          subtitle={`${groupData?.description || ''}`}
          isCollapsable={false}
          onClickBack={handleBackButtonClick}
        ></LuiPageHeader>
        <LuiCard elevation={1} hasCardContentPadding={false} className={classes.card}>
        <Box className={classes.tabBoxMain}>
          <Box sx={{ flexGrow: 1, display: 'flex', height: height - 96 }}>
            <Tabs
              value={value}
              variant="standard"
              orientation="vertical"
              onChange={handleChange}
              className={classes.tabs}
              aria-label="Vertical tabs"
              data-testid="vertical-tabs"
              TabIndicatorProps={{
                style: {
                  left: 0
                }
              }}
            >
              <Tab className={classes.tabTextStyle} label="General" {...a11yProps(0)} />
              {viewPermissionUsers ? <Tab disabled={isDisabledTab} className={classes.tabTextStyle} label="Users" {...a11yProps(1)} /> : null}
              {viewPermissionRoles ? <Tab disabled={isDisabledTab} className={classes.tabTextStyle} label="Roles" {...a11yProps(2)} /> : null}
            </Tabs>
            <TabPanel value={value} index={0}>
              <AddUserGroupGeneralTab
                edit={isEdit}
                groupData={groupData}
                goToAssignUsersTab={(identifier) => {
                  setIsEdit(true);
                  setValue(1);
                  setGroupData(identifier);
                }}
              />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <UsersGroups
                identifier={groupData.identifier}
                goToAssignRolesTab={() => {
                  setValue(2);
                }}
              />
            </TabPanel>
            <TabPanel value={value} index={2}>
              <UserGroupRolesTab
                edit={isEdit}
                identifier={groupData.identifier}
                groupName={groupData.displayName}
                onSave={handleBackButtonClick}
              />
            </TabPanel>
          </Box>
        </Box>
        </LuiCard>
        {loading &&
          <Box alignContent={'center'}>
            <Loader showLoader={true} isCenter />
          </Box>
        }
        {selectSucccessMessage && selectSucccessMessage.showSnackbar && (
          <SnackbarAlert />
        )}
      </Box>
    </>
  );
};

export default AddUserGroup;
